import React, { useState, useEffect } from 'react';
import {
  Button,
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Checkbox
} from 'carbon-components-react';
import Pagination from './pagination';

const Previewer: React.FC<Props> = ({
  open,
  isDownload,
  canDelete,
  title,
  onClose,
  onDownload,
  onDelete,
  onExcludeInclude,
  children,
  enquiryId,
  AOAttachments = [],
  updateAttachments,
  isUpdatingAttachments,
  currentAttachment,
  onNextPreviousButtonClick,
  isAnonymousView
}) => {
  const currentAttachmentId = currentAttachment?.id;
  const [attachmentIsHidden, setAttachmentIsHidden] = useState<boolean>(
    currentAttachment?.is_hidden
  );
  const totalAttachment: number = AOAttachments?.length;
  const [currentAttachmentIndex, setCurrentAttachmentIndex] =
    useState<number>(0);
  const [displayExcludeIncludeButton, setDisplayExcludeIncludeButton] =
    useState<boolean>(false);

  const excludeIncludeButtonClick = (isHidden: boolean) => {
    setAttachmentIsHidden(isHidden);
    const data = [
      {
        attachmentId: currentAttachmentId,
        is_hidden: isHidden,
        enquiryId: enquiryId
      }
    ];
    typeof onExcludeInclude === 'function' && onExcludeInclude(data);
  };

  const handleNextButtonClick = () => {
    const attachmentData = AOAttachments[currentAttachmentIndex];
    const attachmentId = attachmentData.id;
    typeof onNextPreviousButtonClick === 'function' &&
      onNextPreviousButtonClick(attachmentId);
  };

  const handlePrevButtonClick = () => {
    const attachmentData = AOAttachments[currentAttachmentIndex - 2];
    const attachmentId = attachmentData.id;
    typeof onNextPreviousButtonClick === 'function' &&
      onNextPreviousButtonClick(attachmentId);
  };

  useEffect(() => {
    if (AOAttachments) {
      const index = AOAttachments.findIndex(
        (element: any) => element.id === currentAttachmentId
      );
      setCurrentAttachmentIndex(index + 1);
    }
  }, [AOAttachments, currentAttachmentId]);

  useEffect(() => {
    currentAttachment && setAttachmentIsHidden(currentAttachment.is_hidden);
  }, [currentAttachment]);

  useEffect(() => {
    if (!isUpdatingAttachments && updateAttachments) {
      AOAttachments.forEach((attachment: any) => {
        if (attachment.id === currentAttachmentId) {
          setTimeout(() => {
            attachment.is_hidden = attachmentIsHidden;
          }, 300);
        }
      });
    }
  }, [
    updateAttachments,
    isUpdatingAttachments,
    AOAttachments,
    currentAttachmentId,
    attachmentIsHidden
  ]);

  useEffect(() => {
    if (totalAttachment >= 1 && currentAttachmentIndex > 0) {
      setDisplayExcludeIncludeButton(true);
    }
  }, [totalAttachment, currentAttachmentIndex]);

  return (
    <ComposedModal
      className={`previewer-modal${
        displayExcludeIncludeButton ? ' pdf-controller-higher' : ''
      }${isAnonymousView ? ' is-anonymous-view' : ''}`}
      data-testid="excludeFilesModal"
      open={open}
      onClose={() => onClose()}
      preventCloseOnClickOutside={true}
      size="lg"
    >
      <ModalHeader>
        <div className="previewer-modal-header-label">DOCUMENT PREVIEW</div>
        <div className="previewer-modal-header-title">{title}</div>
      </ModalHeader>

      <ModalBody>{children}</ModalBody>
      {isDownload && (
        <ModalFooter
          className={
            displayExcludeIncludeButton
              ? 'include-exclude-extra-footer'
              : canDelete
              ? 'delete-extra-footer'
              : ''
          }
        >
          <div>
            {displayExcludeIncludeButton && (
              <Pagination
                totalAttachment={totalAttachment}
                currentAttachmentIndex={currentAttachmentIndex}
                handlePrevButtonClick={handlePrevButtonClick}
                handleNextButtonClick={handleNextButtonClick}
              />
            )}
          </div>

          <div
            className={`button-wrapper ${
              !displayExcludeIncludeButton && !canDelete ? 'full-width' : ''
            }`}
          >
            {canDelete ? (
              <Button
                kind="danger--tertiary"
                onClick={() => typeof onDelete === 'function' && onDelete()}
                data-testid="excludeInclude"
              >
                Delete
              </Button>
            ) : (
              !isAnonymousView &&
              displayExcludeIncludeButton && (
                <Checkbox
                  labelText="Exclude from combined downloads"
                  id="status-checked-waiting"
                  onChange={(checked: boolean) =>
                    excludeIncludeButtonClick(checked)
                  }
                  checked={attachmentIsHidden}
                />
              )
            )}
            <Button
              kind="primary"
              data-testid="download"
              onClick={() => onDownload()}
            >
              Download
            </Button>
          </div>
        </ModalFooter>
      )}
    </ComposedModal>
  );
};

type Props = {
  open: boolean;
  isDownload: boolean;
  canDelete: boolean;
  title: string;
  type: string;
  onClose: Function;
  onDownload: Function;
  onDelete?: Function;
  onExcludeInclude?: Function;
  children?: React.ReactElement;
  enquiryId?: number;
  AOAttachments?: Array<any>;
  isUpdatingAttachments?: boolean;
  updateAttachments?: any;
  currentAttachment?: any;
  onNextPreviousButtonClick?: Function;
  isAnonymousView?: boolean;
};

export default React.memo(Previewer);
